/**
 * Lightweight CEEG SLA timer helpers shared across worklist and EEG surfaces.
 * Kept outside /features/EEG so worklist routes do not eagerly load EEG core.
 */

export const CEEG_SLA_SCHEMA_VERSION = "ceeg-sla-v1.0.0";

export type CeegSlaTimers = {
  schemaVersion: string;
  studyAgeHours: number;
  lastReviewedAtIso: string | null;
  unreadDurationHours: number;
  seizureCandidateAgeHours: number | null;
  escalationDueInMinutes: number | null;
  escalationOverdue: boolean;
};

export function computeCeegSlaTimers(input: {
  studyStartIso: string;
  lastReviewedAtIso?: string | null;
  oldestPendingCandidateIso?: string | null;
  escalationSlaMinutes?: number;
  now?: Date;
}): CeegSlaTimers {
  const now = input.now || new Date();
  const studyStart = new Date(input.studyStartIso).getTime();
  const lastReviewed = input.lastReviewedAtIso
    ? new Date(input.lastReviewedAtIso).getTime()
    : null;
  const candidate = input.oldestPendingCandidateIso
    ? new Date(input.oldestPendingCandidateIso).getTime()
    : null;
  const studyAgeHours = Math.max(0, (now.getTime() - studyStart) / 3_600_000);
  const unreadDurationHours = lastReviewed
    ? Math.max(0, (now.getTime() - lastReviewed) / 3_600_000)
    : studyAgeHours;
  const seizureCandidateAgeHours =
    candidate == null
      ? null
      : Math.max(0, (now.getTime() - candidate) / 3_600_000);
  const slaMinutes = input.escalationSlaMinutes ?? 60;
  const baseline = lastReviewed ?? studyStart;
  const dueAt = baseline + slaMinutes * 60_000;
  const escalationDueInMinutes = Math.round((dueAt - now.getTime()) / 60_000);
  return {
    schemaVersion: CEEG_SLA_SCHEMA_VERSION,
    studyAgeHours,
    lastReviewedAtIso: input.lastReviewedAtIso ?? null,
    unreadDurationHours,
    seizureCandidateAgeHours,
    escalationDueInMinutes,
    escalationOverdue: escalationDueInMinutes < 0,
  };
}
